import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  LinearProgress,
  useMediaQuery,
  Checkbox,
} from '@mui/material';
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import LanguageIcon from '@mui/icons-material/Language';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../../auth/googleDomainAuth.jsx';
import useApi from '../../hooks/useApi';
import ProductSelectionsTable from '../../components/ProductSelectionsTable.jsx';
import DetailPanelAppSheetTab from '../../components/detailPanel/appsheet/DetailPanelAppSheetTab';
import { scheduleMrtFilterPopoverOpen } from '../../utils/mrtFilterPopover';
import {
  buildCombinedSelectionRowsForTable,
  buildProgramCardModel,
  flattenEnrollmentsForBac,
} from './appsheetAccountsBrowser/dpeViewModel';
import { fuzzyFilterRows, multiSelectFilterFn } from './appsheetAccountsBrowser/fuzzySearch';

const ACCOUNTS_TABLE_NAME = 'SF_GM Salesforce Accounts';
const ROW_LOAD_BATCH = 1000;
const MAX_ROWS_FOR_WIP = 25000;
const HISTORY_APP_SHEET_LIMIT = 800;
const HISTORY_DPE_LIMIT = 500;
const HISTORY_SOURCES = ['DPE Enrollment', 'DPE Selection', 'AppSheet'];
const HISTORY_RANGE_OPTIONS = [30, 60, 90, 120, 180];
const SALESFORCE_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta?.env?.VITE_SALESFORCE_BASE_URL) ||
  'https://cars-commerce.lightning.force.com';

const normalizeType = (value) => String(value ?? '').trim().toLowerCase();
const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();

const loadJson = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_error) {
    return fallback;
  }
};

const saveJson = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Ignore storage errors.
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map((item) => String(item)).join(', ') : 'N/A';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return 'N/A';
    }
  }
  return String(value);
};

const pickFirst = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
};

const resolveDefaultDetailFields = (fields) => {
  const orderedDefaults = [
    'Name',
    'BAC_Code__c',
    'Account_ID__c',
    'CCID__c',
    'Status__c',
    'In_Production__c',
    'Website_Enrollment__c',
    'Address__c',
    'Account_Position__c',
    'SF_Brands__c',
    'ParentId',
    'Ultimate_Parent_Account_Id__c',
  ];

  const available = new Set((fields || []).map((field) => field.name));
  const preferred = orderedDefaults.filter((name) => available.has(name));
  if (preferred.length) return preferred;
  return (fields || []).slice(0, 14).map((field) => field.name);
};

const isReferenceAccountField = (fieldName) => {
  const compact = normalizeKey(fieldName).replace(/[^a-z0-9]/g, '');
  return (
    compact === 'buysellaccountid' ||
    compact === 'buysellaccountidc' ||
    compact === 'parentid' ||
    compact === 'ultimateparentaccountid' ||
    compact === 'ultimateparentaccountidc'
  );
};

const normalizeWebsiteUrl = (raw) => {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(withProtocol);
    return url.toString();
  } catch (_error) {
    return '';
  }
};

const buildColumns = (fields) => {
  return (fields || []).map((field) => {
    const normalizedType = normalizeType(field?.dataType);
    const isEnum = normalizedType === 'enum' || normalizedType === 'enumlist';
    const options = Array.isArray(field?.enumValues)
      ? field.enumValues.map((value) => String(value)).filter(Boolean)
      : [];

    const filterConfig = isEnum
      ? {
          filterVariant: 'multi-select',
          filterFn: multiSelectFilterFn,
          filterSelectOptions: options,
        }
      : {};

    return {
      id: field.name,
      header: field.displayName || field.name,
      size: 180,
      accessorFn: (row) => row?.[field.name],
      ...filterConfig,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (value === null || value === undefined || value === '') return '';
        if (typeof value === 'boolean') return <Chip size="small" label={value ? 'Yes' : 'No'} variant="outlined" />;
        if (Array.isArray(value)) return String(value.join(', '));
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      },
    };
  });
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const normalizeHistoryLookup = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const normalizeLooseKey = (value) => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const getLooseMatchValues = (rowData, candidates) => {
  if (!rowData || typeof rowData !== 'object') return [];
  const target = new Set((candidates || []).map((candidate) => normalizeLooseKey(candidate)).filter(Boolean));
  if (!target.size) return [];
  return Object.entries(rowData)
    .filter(([key]) => target.has(normalizeLooseKey(key)))
    .map(([, value]) => normalizeHistoryLookup(value))
    .filter(Boolean);
};

const toHistoryTimestamp = (value) => {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

export const AppsheetAccountsHistoryTabPanel = ({ bac, accountId, accountName, rowKey = '' }) => {
  const { useEnrollmentStatusChanges, useStatusChanges, useAppSheetStatusChanges } = useApi();
  const [sourceFilters, setSourceFilters] = useState(HISTORY_SOURCES);
  const [historyRangeDays, setHistoryRangeDays] = useState(30);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: 'changedAtRaw', desc: true }]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnPinning, setColumnPinning] = useState({ left: ['changedAt', 'source'], right: [] });
  const [pendingFilterColumnId, setPendingFilterColumnId] = useState(null);

  const historyDateWindow = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - historyRangeDays * 24 * 60 * 60 * 1000);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [historyRangeDays]);

  const { data: enrollmentHistory = [], isLoading: loadingEnrollmentHistory, error: enrollmentHistoryError } =
    useEnrollmentStatusChanges({
      bac,
      startDate: historyDateWindow.startDate,
      endDate: historyDateWindow.endDate,
      limit: HISTORY_DPE_LIMIT,
    });
  const { data: selectionHistory = [], isLoading: loadingSelectionHistory, error: selectionHistoryError } = useStatusChanges({
    bac,
    startDate: historyDateWindow.startDate,
    endDate: historyDateWindow.endDate,
    limit: HISTORY_DPE_LIMIT,
  });
  const {
    data: appSheetHistoryPayload,
    isLoading: loadingAppSheetHistory,
    error: appSheetHistoryError,
  } = useAppSheetStatusChanges({
    startDate: historyDateWindow.startDate,
    endDate: historyDateWindow.endDate,
    limit: HISTORY_APP_SHEET_LIMIT,
  });

  const normalizedAccountId = useMemo(() => normalizeHistoryLookup(accountId), [accountId]);
  const normalizedBac = useMemo(() => normalizeHistoryLookup(bac), [bac]);
  const normalizedAccountName = useMemo(() => normalizeHistoryLookup(accountName), [accountName]);
  const normalizedRowKey = useMemo(() => normalizeHistoryLookup(rowKey), [rowKey]);

  const appSheetRows = useMemo(() => {
    const rows = Array.isArray(appSheetHistoryPayload?.rows) ? appSheetHistoryPayload.rows : [];
    if (!rows.length) return [];

    const bacCandidates = [
      'bac',
      'BAC',
      'BAC_Code__c',
      'BAC_Code',
      'bacCode',
      'BAC Code',
      'Dealer BAC',
      'Dealer_BAC',
      'Dealer_BAC__c',
    ];
    const accountCandidates = [
      'Account_ID__c',
      'Account_ID',
      'accountId',
      'account_id',
      'AccountId',
      'Id',
      'ID',
      'rowKey',
    ];
    const nameCandidates = ['Name', 'AccountName', 'accountName', 'DealerName', 'dealerName', 'Account_Name__c'];

    return rows.filter((entry) => {
      const rowData = entry?.rowData && typeof entry.rowData === 'object' ? entry.rowData : {};
      const entryRowKey = normalizeHistoryLookup(entry?.rowKey || rowData?.rowKey || rowData?.Id || rowData?.id);
      const accountValues = [
        ...accountCandidates.map((key) => normalizeHistoryLookup(rowData?.[key])).filter(Boolean),
        ...getLooseMatchValues(rowData, accountCandidates),
      ];
      const bacValues = [
        ...bacCandidates.map((key) => normalizeHistoryLookup(rowData?.[key])).filter(Boolean),
        ...getLooseMatchValues(rowData, bacCandidates),
      ];
      const nameValues = [
        ...nameCandidates.map((key) => normalizeHistoryLookup(rowData?.[key])).filter(Boolean),
        ...getLooseMatchValues(rowData, nameCandidates),
      ];

      const accountMatch =
        normalizedAccountId && (entryRowKey === normalizedAccountId || accountValues.includes(normalizedAccountId));
      const bacMatch = normalizedBac && bacValues.includes(normalizedBac);
      const nameMatch = normalizedAccountName && nameValues.includes(normalizedAccountName);
      const rowKeyMatch = normalizedRowKey && entryRowKey === normalizedRowKey;
      return Boolean(accountMatch || bacMatch || nameMatch || rowKeyMatch);
    });
  }, [appSheetHistoryPayload?.rows, normalizedAccountId, normalizedBac, normalizedAccountName, normalizedRowKey]);

  const rows = useMemo(() => {
    const mappedEnrollment = (Array.isArray(enrollmentHistory) ? enrollmentHistory : []).map((entry) => ({
      id: `history-enrollment-${entry?.changeId ?? entry?.id}`,
      changedAt: entry?.changedAt || null,
      source: 'DPE Enrollment',
      programName: entry?.programName || '',
      eventLabel: 'Enrollment Status',
      previousValue: entry?.previousStatus ?? '',
      newValue: entry?.newStatus ?? '',
      rowKey: entry?.bac ? `BAC ${entry.bac}` : '',
    }));

    const mappedSelection = (Array.isArray(selectionHistory) ? selectionHistory : []).map((entry) => ({
      id: `history-selection-${entry?.changeId ?? entry?.id}`,
      changedAt: entry?.changedAt || null,
      source: 'DPE Selection',
      programName: entry?.programName || '',
      eventLabel: entry?.serviceItemName || entry?.referenceId || 'Product Selection',
      previousValue: entry?.previousStatus ?? '',
      newValue: entry?.newStatus ?? '',
      rowKey: entry?.selectionKey || entry?.selectionReferenceId || entry?.referenceId || '',
    }));

    const mappedAppSheet = appSheetRows.map((entry) => ({
      id: `history-appsheet-${entry?.id}`,
      changedAt: entry?.changedAt || null,
      source: 'AppSheet',
      programName: entry?.programName || '',
      eventLabel: entry?.rowData?.Product_Name || entry?.rowData?.Name || 'OrderItem Status',
      previousValue: entry?.statusOld ?? '',
      newValue: entry?.statusNew ?? '',
      rowKey: entry?.rowKey || entry?.rowData?.Id || '',
    }));

    return [...mappedEnrollment, ...mappedSelection, ...mappedAppSheet]
      .sort((a, b) => toHistoryTimestamp(b.changedAt) - toHistoryTimestamp(a.changedAt))
      .map((row, index) => ({
        ...row,
        changedAtRaw: toHistoryTimestamp(row.changedAt),
        id: row.id || `history-fallback-${index}`,
      }));
  }, [enrollmentHistory, selectionHistory, appSheetRows]);

  const sourceCounts = useMemo(() => {
    const counts = { 'DPE Enrollment': 0, 'DPE Selection': 0, AppSheet: 0 };
    rows.forEach((row) => {
      if (counts[row.source] !== undefined) counts[row.source] += 1;
    });
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!sourceFilters.length) return [];
    const active = new Set(sourceFilters);
    return rows.filter((row) => active.has(row.source));
  }, [rows, sourceFilters]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'changedAt',
        headerName: 'Changed At',
        header: 'Changed At',
        id: 'changedAt',
        minWidth: 180,
        size: 180,
        filterVariant: 'text',
        sortingFn: (a, b) => (a.original.changedAtRaw || 0) - (b.original.changedAtRaw || 0),
        Cell: ({ row }) => formatDateTime(row?.original?.changedAt),
      },
      {
        accessorKey: 'source',
        header: 'Source',
        size: 170,
        filterVariant: 'multi-select',
        filterFn: multiSelectFilterFn,
        filterSelectOptions: HISTORY_SOURCES,
      },
      { accessorKey: 'programName', header: 'Program', size: 150 },
      { accessorKey: 'eventLabel', header: 'Event', size: 260 },
      { accessorKey: 'previousValue', header: 'From', size: 150 },
      { accessorKey: 'newValue', header: 'To', size: 150 },
      { accessorKey: 'rowKey', header: 'Row Key', size: 220 },
    ],
    []
  );

  const loading = loadingEnrollmentHistory || loadingSelectionHistory || loadingAppSheetHistory;
  const errorMessage =
    enrollmentHistoryError?.message || selectionHistoryError?.message || appSheetHistoryError?.message || '';

  const handleSourceFilterChange = (event) => {
    const value = event.target.value;
    setSourceFilters(Array.isArray(value) ? value : []);
  };

  const historyTable = useMaterialReactTable({
    columns,
    data: filteredRows,
    localization: MRT_Localization_EN,
    getRowId: (row) => String(row?.id ?? Math.random()),
    enableRowSelection: false,
    enableColumnOrdering: true,
    enableColumnDragging: false,
    enableColumnResizing: true,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableSorting: true,
    state: {
      globalFilter,
      columnFilters,
      sorting,
      columnVisibility,
      columnOrder,
      columnPinning,
      isLoading: loading,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    initialState: {
      density: 'compact',
      showGlobalFilter: true,
      pagination: { pageIndex: 0, pageSize: 25 },
      sorting: [{ id: 'changedAt', desc: true }],
      columnPinning,
      columnOrder: columnOrder.length ? columnOrder : undefined,
    },
    muiSearchTextFieldProps: {
      placeholder: 'Search history across all fields...',
      size: 'small',
      sx: { minWidth: 320, maxWidth: 460 },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100],
    },
    renderColumnActionsMenuItems: ({ column, closeMenu, internalColumnMenuItems, table }) => {
      const canFilter = column.getCanFilter?.();
      if (!canFilter) return internalColumnMenuItems;

      const addFilterItem = (
        <MenuItem
          key="add-filter"
          onClick={() => {
            setPendingFilterColumnId(column.id);
            closeMenu?.();
          }}
        >
          <FilterAltIcon fontSize="small" style={{ marginRight: 8 }} />
          Add Filter
        </MenuItem>
      );

      const internalItems = Array.isArray(internalColumnMenuItems)
        ? [...internalColumnMenuItems]
        : internalColumnMenuItems
          ? [internalColumnMenuItems]
          : [];

      const getMenuItemLabel = (node) => {
        if (!node) return '';
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(getMenuItemLabel).join('');
        if (typeof node === 'object') {
          if (node.props?.label) return node.props.label;
          if (node.props?.children) return getMenuItemLabel(node.props.children);
        }
        return '';
      };

      const clearFilterLabel = table?.options?.localization?.clearFilter || 'Clear Filter';
      const normalizedClearFilterLabel = clearFilterLabel.replace(/\s+/g, ' ').trim().toLowerCase();
      const clearFilterIndex = internalItems.findIndex((item) => {
        const label = getMenuItemLabel(item);
        const normalizedLabel = label.replace(/\s+/g, ' ').trim().toLowerCase();
        return normalizedLabel === normalizedClearFilterLabel;
      });

      if (clearFilterIndex === -1) return [addFilterItem, ...internalItems];
      internalItems.splice(clearFilterIndex, 0, addFilterItem);
      return internalItems;
    },
    muiTableHeadCellProps: ({ column }) => {
      const isSorted = Boolean(column.getIsSorted?.());
      return {
        sx: {
          '& .Mui-TableHeadCell-Content button[aria-label*="Filter by"]': {
            opacity: 0,
            pointerEvents: 'none',
          },
          '& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root': {
            display: 'inline-flex',
          },
          '& .MuiTableSortLabel-root': {
            display: isSorted ? 'inline-flex' : 'none',
          },
        },
      };
    },
    renderToolbarInternalActions: ({ table }) => {
      const {
        columnFilterDisplayMode,
        enableColumnFilters,
        enableColumnOrdering,
        enableColumnPinning,
        enableDensityToggle,
        enableFilters,
        enableFullScreenToggle,
        enableGlobalFilter,
        enableHiding,
        initialState,
      } = table.options;
      const { columnFilters: activeColumnFilters, globalFilter: activeGlobalFilter } = table.getState();
      const hasActiveFilters = (activeColumnFilters?.length || 0) > 0 || Boolean(String(activeGlobalFilter || '').trim());
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {enableFilters && enableGlobalFilter && !(initialState?.showGlobalFilter) ? (
            <MRT_ToggleGlobalFilterButton table={table} />
          ) : null}
          <Tooltip title="Reset filters">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  setColumnFilters([]);
                  setGlobalFilter('');
                }}
                disabled={!hasActiveFilters}
              >
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {enableFilters && enableColumnFilters && columnFilterDisplayMode !== 'popover' ? (
            <MRT_ToggleFiltersButton table={table} />
          ) : null}
          {(enableHiding || enableColumnOrdering || enableColumnPinning) ? (
            <MRT_ShowHideColumnsButton table={table} />
          ) : null}
          {enableDensityToggle ? <MRT_ToggleDensePaddingButton table={table} /> : null}
          {enableFullScreenToggle ? <MRT_ToggleFullScreenButton table={table} /> : null}
        </Box>
      );
    },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.5 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="history-range-days-label">Date Range</InputLabel>
          <Select
            labelId="history-range-days-label"
            id="history-range-days"
            label="Date Range"
            value={historyRangeDays}
            onChange={(event) => setHistoryRangeDays(Number(event.target.value) || 30)}
          >
            {HISTORY_RANGE_OPTIONS.map((days) => (
              <MenuItem key={days} value={days}>
                {days} Days
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 230 }}>
          <InputLabel id="history-source-filters-label">Sources</InputLabel>
          <Select
            labelId="history-source-filters-label"
            id="history-source-filters"
            multiple
            value={sourceFilters}
            onChange={handleSourceFilterChange}
            label="Sources"
            renderValue={(selected) => {
              if (!Array.isArray(selected) || selected.length === 0) return 'No sources';
              return selected.join(', ');
            }}
          >
            {HISTORY_SOURCES.map((source) => (
              <MenuItem key={source} value={source}>
                <Checkbox size="small" checked={sourceFilters.includes(source)} />
                <ListItemText primary={`${source} (${(sourceCounts[source] || 0).toLocaleString()})`} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    ),
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 360px)',
      },
    },
  });

  useEffect(() => {
    if (!pendingFilterColumnId || !historyTable) return;
    return scheduleMrtFilterPopoverOpen({
      table: historyTable,
      columnId: pendingFilterColumnId,
      buttonSelector: 'button[aria-label*="Filter by"]',
      onComplete: () => setPendingFilterColumnId(null),
    });
  }, [pendingFilterColumnId, historyTable]);

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {!bac ? <Alert severity="info">History view is disabled for accounts without BAC_Code__c.</Alert> : null}
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Unified Change History
          </Typography>
          <Chip size="small" variant="outlined" label={`Range: ${historyRangeDays} days`} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Enrollment status, DPE product selection status, and AppSheet order item status changes in one timeline.
        </Typography>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ flex: 1, minHeight: 0, borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {loading ? <LinearProgress /> : null}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <MaterialReactTable table={historyTable} />
        </Box>
      </Paper>
    </Box>
  );
};

const ProgramMetricCard = ({ model }) => {
  const [showDate, setShowDate] = useState(false);

  return (
    <Card
      variant="outlined"
      onMouseEnter={() => setShowDate(true)}
      onMouseLeave={() => setShowDate(false)}
      onClick={() => setShowDate((prev) => !prev)}
      sx={{
        cursor: 'pointer',
        borderColor: model.isEnrolled ? 'success.light' : 'error.light',
        borderRadius: 2.5,
        minHeight: 112,
        background: model.isEnrolled
          ? 'linear-gradient(145deg, rgba(46,125,50,0.10), rgba(255,255,255,0) 55%)'
          : 'linear-gradient(145deg, rgba(211,47,47,0.10), rgba(255,255,255,0) 55%)',
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {model.programName}
        </Typography>
        <Chip
          size="small"
          color={model.isEnrolled ? 'success' : 'error'}
          label={showDate ? `${model.hoverDateLabel}: ${model.hoverDateDisplay}` : String(model.isEnrolled)}
          variant={showDate ? 'outlined' : 'filled'}
        />
        <Typography variant="caption" color="text.secondary">
          Status: {model.status}
        </Typography>
      </CardContent>
    </Card>
  );
};

const AccountFieldItem = ({
  row,
  index,
  total,
  pinned,
  showActions,
  onPinToggle,
  onHide,
  onCopyValue,
  onOpenReference,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) => {
  const isReference = isReferenceAccountField(row.name);
  return (
    <Box
      data-account-field={row.name}
      data-account-field-pinned={pinned ? 'true' : 'false'}
      onDragOver={(event) => onDragOver(event)}
      onDrop={(event) => onDrop(event, row.name)}
      sx={{
        py: 1.1,
        px: 1,
        borderRadius: 2,
        bgcolor: pinned ? 'action.hover' : 'transparent',
        borderBottom: index === total - 1 ? 'none' : '1px solid',
        borderColor: 'divider',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 1,
        alignItems: 'center',
      }}
    >
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.2, fontWeight: 600, userSelect: 'text', WebkitUserSelect: 'text' }}
        >
          {row.label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            wordBreak: 'break-word',
            fontSize: '0.98rem',
            userSelect: 'text',
            WebkitUserSelect: 'text',
            cursor: 'text',
          }}
        >
          {formatValue(row.value)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          opacity: showActions ? 1 : 0,
          transition: 'opacity 120ms ease',
        }}
      >
        <Tooltip title="Copy value">
          <IconButton
            size="small"
            aria-label={`Copy ${row.label}`}
            onClick={(event) => {
              event.stopPropagation();
              onCopyValue(row.value);
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {isReference ? (
          <Tooltip title="Open linked account">
            <IconButton
              size="small"
              aria-label={`Open ${row.label}`}
              onClick={(event) => {
                event.stopPropagation();
                onOpenReference(row.value);
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip title="Drag to reorder">
          <IconButton
            size="small"
            aria-label={`Drag ${row.label}`}
            draggable
            onDragStart={(event) => onDragStart(event, row.name)}
            onDragEnd={onDragEnd}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={pinned ? 'Unpin field' : 'Pin field'}>
          <IconButton
            size="small"
            aria-label={`Pin ${row.label}`}
            onClick={(event) => {
              event.stopPropagation();
              onPinToggle(row.name);
            }}
          >
            {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Hide field">
          <IconButton
            size="small"
            aria-label={`Hide ${row.label}`}
            onClick={(event) => {
              event.stopPropagation();
              onHide(row.name);
            }}
          >
            <VisibilityOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const AppsheetAccountsAccountTabPanel = ({
  selectedRow,
  allRows,
  tableFields,
  selectedAccountId,
  userKey,
  onSelectRow,
}) => {
  const detailFieldStorageKey = `wipAppsheetAccountsDetailFields:${userKey || 'guest'}`;
  const pinnedFieldStorageKey = `wipAppsheetAccountsPinnedFields:${userKey || 'guest'}`;
  const [detailFieldNames, setDetailFieldNames] = useState([]);
  const [pinnedFieldNames, setPinnedFieldNames] = useState([]);
  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);
  const [hoveredFieldName, setHoveredFieldName] = useState('');
  const [activeFieldName, setActiveFieldName] = useState('');
  const [draggingFieldName, setDraggingFieldName] = useState('');
  const [detailFieldsReady, setDetailFieldsReady] = useState(false);
  const isHoverCapable = useMediaQuery('(hover: hover) and (pointer: fine)');

  useEffect(() => {
    const availableFieldNames = new Set((tableFields || []).map((field) => field.name));
    const persistedDetailFields = loadJson(detailFieldStorageKey, []);
    const persistedPinnedFields = loadJson(pinnedFieldStorageKey, []);
    const sanitizedPersisted = Array.isArray(persistedDetailFields)
      ? persistedDetailFields.filter((field) => availableFieldNames.has(field))
      : [];
    const nextDetailFields = sanitizedPersisted.length
      ? sanitizedPersisted
      : resolveDefaultDetailFields(tableFields || []);
    const sanitizedPinned = Array.isArray(persistedPinnedFields)
      ? persistedPinnedFields.filter((field) => nextDetailFields.includes(field))
      : [];
    setDetailFieldNames(nextDetailFields);
    setPinnedFieldNames(sanitizedPinned);
    setDetailFieldsReady(true);
  }, [detailFieldStorageKey, pinnedFieldStorageKey, tableFields]);

  useEffect(() => {
    if (!detailFieldsReady) return;
    saveJson(detailFieldStorageKey, detailFieldNames);
  }, [detailFieldStorageKey, detailFieldNames, detailFieldsReady]);

  useEffect(() => {
    if (!detailFieldsReady) return;
    saveJson(pinnedFieldStorageKey, pinnedFieldNames);
  }, [pinnedFieldStorageKey, pinnedFieldNames, detailFieldsReady]);

  const accountIdMap = useMemo(() => {
    const map = new Map();
    (allRows || []).forEach((row) => {
      const accountId = String(row?.Account_ID__c || '').trim();
      if (!accountId) return;
      map.set(accountId.toLowerCase(), row);
    });
    return map;
  }, [allRows]);

  const detailFieldOptions = useMemo(
    () =>
      (tableFields || []).map((field) => ({
        label: field.displayName || field.name,
        value: field.name,
      })),
    [tableFields]
  );

  const detailRows = useMemo(() => {
    if (!selectedRow) return [];
    const fieldsByName = new Map((tableFields || []).map((field) => [field.name, field]));
    return detailFieldNames
      .map((fieldName) => fieldsByName.get(fieldName))
      .filter(Boolean)
      .map((field) => ({
        name: field.name,
        label: field.displayName || field.name,
        value: selectedRow?.[field.name],
      }));
  }, [detailFieldNames, selectedRow, tableFields]);

  const pinnedRows = useMemo(() => {
    const pinnedSet = new Set(pinnedFieldNames);
    return detailRows.filter((row) => pinnedSet.has(row.name));
  }, [detailRows, pinnedFieldNames]);

  const nonPinnedRows = useMemo(() => {
    const pinnedSet = new Set(pinnedFieldNames);
    return detailRows.filter((row) => !pinnedSet.has(row.name));
  }, [detailRows, pinnedFieldNames]);

  const nonPinnedColumns = useMemo(() => {
    const left = [];
    const right = [];
    nonPinnedRows.forEach((row, index) => {
      if (index % 2 === 0) left.push(row);
      else right.push(row);
    });
    return [left, right];
  }, [nonPinnedRows]);

  const accountHighlights = useMemo(() => {
    if (!selectedRow) return [];
    return [
      { label: 'Status', value: pickFirst(selectedRow, ['Status__c', 'status']) },
      { label: 'In Production', value: pickFirst(selectedRow, ['In_Production__c']) },
      { label: 'Website Enrollment', value: pickFirst(selectedRow, ['Website_Enrollment__c']) },
      { label: 'Position', value: pickFirst(selectedRow, ['Account_Position__c']) },
      { label: 'Brands', value: pickFirst(selectedRow, ['SF_Brands__c']) },
    ].filter((item) => item.value !== null && item.value !== undefined && item.value !== '');
  }, [selectedRow]);

  const websiteUrl = useMemo(() => {
    const value = pickFirst(selectedRow, [
      'Website_URL__c',
      'Website Url',
      'WebsiteUrl',
      'Website',
      'websiteUrl',
      'website',
      'url',
      'URL',
    ]);
    return normalizeWebsiteUrl(value);
  }, [selectedRow]);

  const salesforceUrl = useMemo(() => {
    if (!selectedAccountId) return '';
    return `${SALESFORCE_BASE_URL.replace(/\/$/, '')}/${encodeURIComponent(selectedAccountId)}`;
  }, [selectedAccountId]);

  const openExternal = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openReferenceAccount = (accountIdValue) => {
    const key = String(accountIdValue || '').trim().toLowerCase();
    if (!key) return;
    const linkedRow = accountIdMap.get(key);
    if (linkedRow) {
      onSelectRow?.(linkedRow);
      return;
    }
    const fallbackUrl = `${SALESFORCE_BASE_URL.replace(/\/$/, '')}/${encodeURIComponent(String(accountIdValue).trim())}`;
    openExternal(fallbackUrl);
  };

  const handleCopyFieldValue = async (value) => {
    let toCopy = '';
    if (value !== null && value !== undefined) {
      if (typeof value === 'object') {
        try {
          toCopy = JSON.stringify(value);
        } catch (_error) {
          toCopy = String(value);
        }
      } else {
        toCopy = String(value);
      }
    }
    if (!toCopy) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(toCopy);
        return;
      }
    } catch (_error) {
      // noop fallback below
    }
    console.warn('[AppSheetAccounts] Clipboard access is blocked; field value was not copied.');
  };

  const handleFieldDragStart = (event, fieldName) => {
    setDraggingFieldName(fieldName);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/x-gmer-account-field', fieldName);
    event.dataTransfer.setData('text/plain', fieldName);
  };

  const handleFieldDragEnd = () => {
    setDraggingFieldName('');
  };

  const handleFieldDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleFieldDrop = (event, targetFieldName) => {
    event.preventDefault();
    const sourceFieldName =
      draggingFieldName ||
      event.dataTransfer.getData('application/x-gmer-account-field') ||
      event.dataTransfer.getData('text/plain');
    if (!sourceFieldName || !targetFieldName || sourceFieldName === targetFieldName) return;

    setDetailFieldNames((prev) => {
      const next = [...prev];
      const sourceIndex = next.indexOf(sourceFieldName);
      const targetIndex = next.indexOf(targetFieldName);
      if (sourceIndex < 0 || targetIndex < 0) return prev;
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setDraggingFieldName('');
  };

  const handleFieldPinToggle = (fieldName) => {
    setPinnedFieldNames((prev) => {
      if (prev.includes(fieldName)) return prev.filter((item) => item !== fieldName);
      return [...prev, fieldName];
    });
  };

  const handleFieldHide = (fieldName) => {
    setDetailFieldNames((prev) => prev.filter((item) => item !== fieldName));
    setPinnedFieldNames((prev) => prev.filter((item) => item !== fieldName));
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 0.5 }}>
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          p: 2.25,
          borderRadius: 3,
          background:
            'linear-gradient(140deg, rgba(25,118,210,0.08), rgba(255,255,255,0) 42%), linear-gradient(320deg, rgba(46,125,50,0.08), rgba(255,255,255,0) 42%)',
        }}
      >
        <Typography variant="overline" color="text.secondary">
          Account Snapshot
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {selectedRow?.Name || 'Unknown Account'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudQueueIcon fontSize="small" />}
            onClick={() => openExternal(salesforceUrl)}
            disabled={!salesforceUrl}
          >
            Open in Salesforce
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LanguageIcon fontSize="small" />}
            onClick={() => openExternal(websiteUrl)}
            disabled={!websiteUrl}
          >
            Open Website
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon fontSize="small" />}
            onClick={() => setFieldsModalOpen(true)}
          >
            Modify Fields
          </Button>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          {accountHighlights.map((item, index) => (
            <Box
              key={item.label}
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: index % 2 === 0 ? 'rgba(255,255,255,0.5)' : 'transparent',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatValue(item.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }} id="account-fields-panel">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Visible Account Fields
          </Typography>
          <Chip size="small" variant="outlined" label={`${detailRows.length} fields`} />
        </Box>

        {pinnedRows.length ? (
          <Box
            id="account-pinned-fields"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              mb: 1,
            }}
          >
            {pinnedRows.map((row, index) => {
              const showActions = isHoverCapable ? hoveredFieldName === row.name : activeFieldName === row.name;
              return (
                <Box
                  key={row.name}
                  onMouseEnter={() => setHoveredFieldName(row.name)}
                  onMouseLeave={() => setHoveredFieldName('')}
                  onClick={() => {
                    if (isHoverCapable) return;
                    setActiveFieldName((prev) => (prev === row.name ? '' : row.name));
                  }}
                >
                  <AccountFieldItem
                    row={row}
                    index={index}
                    total={pinnedRows.length}
                    pinned
                    showActions={showActions}
                    onPinToggle={handleFieldPinToggle}
                    onHide={handleFieldHide}
                    onCopyValue={handleCopyFieldValue}
                    onOpenReference={openReferenceAccount}
                    onDragStart={handleFieldDragStart}
                    onDragEnd={handleFieldDragEnd}
                    onDragOver={handleFieldDragOver}
                    onDrop={handleFieldDrop}
                  />
                </Box>
              );
            })}
          </Box>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: 0.5, md: 2 },
          }}
        >
          {nonPinnedColumns.map((columnRows, columnIndex) => (
            <Box key={`col-${columnIndex}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {columnRows.map((row, index) => {
                const showActions = isHoverCapable ? hoveredFieldName === row.name : activeFieldName === row.name;
                return (
                  <Box
                    key={row.name}
                    onMouseEnter={() => setHoveredFieldName(row.name)}
                    onMouseLeave={() => setHoveredFieldName('')}
                    onClick={() => {
                      if (isHoverCapable) return;
                      setActiveFieldName((prev) => (prev === row.name ? '' : row.name));
                    }}
                  >
                    <AccountFieldItem
                      row={row}
                      index={index}
                      total={columnRows.length}
                      pinned={false}
                      showActions={showActions}
                      onPinToggle={handleFieldPinToggle}
                      onHide={handleFieldHide}
                      onCopyValue={handleCopyFieldValue}
                      onOpenReference={openReferenceAccount}
                      onDragStart={handleFieldDragStart}
                      onDragEnd={handleFieldDragEnd}
                      onDragOver={handleFieldDragOver}
                      onDrop={handleFieldDrop}
                    />
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>

      <Dialog open={fieldsModalOpen} onClose={() => setFieldsModalOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Modify Visible Account Fields</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={detailFieldOptions}
            value={detailFieldOptions.filter((option) => detailFieldNames.includes(option.value))}
            onChange={(_event, value) => {
              const next = value.map((item) => item.value);
              setDetailFieldNames(next);
              setPinnedFieldNames((prev) => prev.filter((item) => next.includes(item)));
            }}
            getOptionLabel={(option) => option.label}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField {...params} label="Fields" placeholder="Add or remove fields shown in the Account tab" />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldsModalOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const AppsheetAccountsDpeTabPanel = ({
  hasSelectedBac,
  selectedBac,
  dpeError,
  loadingDpeData,
  programCardModels,
  combinedSelectionRowsForTable,
  userEmail,
}) => (
  <Box sx={{ height: '100%', minHeight: 0, pr: 0.5, display: 'flex', flexDirection: 'column' }}>
    {!hasSelectedBac ? <Alert severity="info">DPE view is disabled for accounts without BAC_Code__c.</Alert> : null}
    {hasSelectedBac && dpeError ? <Alert severity="error">{dpeError?.message || 'Failed to load DPE data.'}</Alert> : null}
    {hasSelectedBac && loadingDpeData ? (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    ) : null}
    {hasSelectedBac && !loadingDpeData ? (
      <>
        <Box
          sx={{
            flexShrink: 0,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          {programCardModels.length ? (
            programCardModels.map((model) => <ProgramMetricCard key={`${model.programName}:${model.status}`} model={model} />)
          ) : (
            <Alert severity="info">No DPE enrollments found for BAC {selectedBac}.</Alert>
          )}
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            bgcolor: 'rgba(25,118,210,0.02)',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            DPE Product Selections (All Programs)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Combined product selections for BAC {selectedBac} across every enrolled program.
          </Typography>

          <ProductSelectionsTable
            rows={combinedSelectionRowsForTable}
            userEmail={userEmail || null}
            onNavigateToDealer={() => {}}
            onOpenDetail={() => {}}
            isMaximized={false}
            enableInternalScroll
            enableVmtQuickActions
            refreshProgramName={null}
          />
        </Paper>
      </>
    ) : null}
  </Box>
);

export const AppsheetAccountsBuySellsTabPanel = ({
  buySellsError,
  loadingBuySells,
  relatedBuySells,
  buySellColumns,
  selectedAccountId,
}) => (
  <Box sx={{ height: '100%', overflow: 'auto', pr: 0.5 }}>
    {buySellsError ? <Alert severity="error">{buySellsError?.message || 'Failed to load buy/sells.'}</Alert> : null}
    {loadingBuySells ? (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    ) : null}
    {!loadingBuySells && !buySellsError ? (
      <>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Related Buy/Sells
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Matching rows where selling or buying account ID equals {selectedAccountId || 'current account'}.
        </Typography>
        <Paper variant="outlined" sx={{ height: 420 }}>
          <DataGrid
            rows={relatedBuySells}
            columns={buySellColumns}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
            sx={{ border: 0 }}
          />
        </Paper>
      </>
    ) : null}
  </Box>
);

const WipAppsheetAccountsBrowserPage = () => {
  const location = useLocation();
  const { user } = useAuth() || {};
  const { useAllEnrollmentData, useBuySells } = useApi();
  const { data: allEnrollmentData, isLoading: loadingDpeData, error: dpeError } = useAllEnrollmentData();
  const {
    data: buySellData,
    isLoading: loadingBuySells,
    error: buySellsError,
  } = useBuySells();

  const userKey = user?.email ? String(user.email).toLowerCase() : 'guest';
  const tableColumnStorageKey = `wipAppsheetAccountsColumns:${userKey}`;
  const tableStateStorageKey = `wipAppsheetAccountsTableState:${userKey}`;

  const [tableMeta, setTableMeta] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [isLoadingRows, setIsLoadingRows] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading AppSheet accounts...');
  const [loadError, setLoadError] = useState('');

  const savedColumnState = useMemo(
    () => loadJson(tableColumnStorageKey, { visibility: {}, ordering: [], pinning: { left: [], right: [] } }),
    [tableColumnStorageKey]
  );
  const savedTableState = useMemo(
    () => loadJson(tableStateStorageKey, { columnFilters: [], sorting: [] }),
    [tableStateStorageKey]
  );

  const [columnVisibility, setColumnVisibility] = useState(savedColumnState.visibility || {});
  const [columnOrder, setColumnOrder] = useState(
    Array.isArray(savedColumnState.ordering) ? savedColumnState.ordering : []
  );
  const [columnPinning, setColumnPinning] = useState(
    savedColumnState.pinning || { left: ['Name', 'BAC_Code__c', 'Account_ID__c'], right: [] }
  );
  const [columnFilters, setColumnFilters] = useState(
    Array.isArray(savedTableState.columnFilters) ? savedTableState.columnFilters : []
  );
  const [sorting, setSorting] = useState(Array.isArray(savedTableState.sorting) ? savedTableState.sorting : []);
  const [pendingFilterColumnId, setPendingFilterColumnId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('account');
  const isWipRoute = String(location?.pathname || '').startsWith('/__wip/');

  useEffect(() => {
    saveJson(tableColumnStorageKey, {
      visibility: columnVisibility,
      ordering: columnOrder,
      pinning: columnPinning,
    });
  }, [tableColumnStorageKey, columnVisibility, columnOrder, columnPinning]);

  useEffect(() => {
    saveJson(tableStateStorageKey, { columnFilters, sorting });
  }, [tableStateStorageKey, columnFilters, sorting]);

  useEffect(() => {
    let active = true;

    const loadAllRows = async () => {
      setIsLoadingRows(true);
      setLoadError('');
      setLoadingMessage('Loading AppSheet accounts table metadata...');

      try {
        const { data: tablesData } = await axios.get('/api/appsheet/tables');
        const tables = Array.isArray(tablesData?.tables) ? tablesData.tables : [];
        const accountsTable = tables.find((table) => table?.name === ACCOUNTS_TABLE_NAME);

        if (!accountsTable) {
          throw new Error(`AppSheet table "${ACCOUNTS_TABLE_NAME}" is not configured.`);
        }

        if (!active) return;
        setTableMeta(accountsTable);

        let cursor;
        let hasMore = true;
        let loadedRows = [];

        while (hasMore) {
          setLoadingMessage(`Loading AppSheet accounts rows (${loadedRows.length.toLocaleString()} loaded)...`);
          const { data: pageData } = await axios.post(
            `/api/appsheet/tables/${encodeURIComponent(accountsTable.id)}/rows/query`,
            {
              limit: ROW_LOAD_BATCH,
              cursorId: cursor?.id,
              cursorGenerationId: cursor?.generationId,
              includeTotal: false,
            }
          );

          const pageRows = Array.isArray(pageData?.rows) ? pageData.rows : [];
          loadedRows = loadedRows.concat(pageRows);

          if (loadedRows.length > MAX_ROWS_FOR_WIP) {
            throw new Error(
              `WIP browser row cap exceeded (${MAX_ROWS_FOR_WIP.toLocaleString()}). Refine upstream data before loading all rows.`
            );
          }

          const nextCursor = pageData?.nextCursor;
          if (!nextCursor?.id || pageRows.length === 0) {
            hasMore = false;
          } else {
            cursor = nextCursor;
          }
        }

        if (!active) return;

        setAllRows(loadedRows);
      } catch (error) {
        if (!active) return;
        setLoadError(error?.response?.data?.error || error?.message || 'Failed to load AppSheet accounts data.');
      } finally {
        if (active) {
          setIsLoadingRows(false);
          setLoadingMessage('');
        }
      }
    };

    void loadAllRows();

    return () => {
      active = false;
    };
  }, []);

  const fieldNames = useMemo(() => (tableMeta?.fields || []).map((field) => field.name), [tableMeta?.fields]);
  const filteredRows = useMemo(
    () => fuzzyFilterRows(allRows, searchQuery, fieldNames),
    [allRows, searchQuery, fieldNames]
  );
  const tableColumns = useMemo(() => buildColumns(tableMeta?.fields || []), [tableMeta?.fields]);

  const searchSuggestions = useMemo(() => {
    const raw = String(searchQuery || '').trim().toLowerCase();
    if (!raw) return [];
    const byLabel = new Map();

    for (const row of allRows) {
      const bac = String(row?.BAC_Code__c || '').trim();
      const name = String(row?.Name || '').trim();
      const accountId = String(row?.Account_ID__c || '').trim();
      const labels = [
        bac ? { label: `${bac} · BAC`, value: bac } : null,
        name ? { label: `${name} · Dealer`, value: name } : null,
        accountId ? { label: `${accountId} · Account ID`, value: accountId } : null,
      ].filter(Boolean);

      for (const item of labels) {
        if (!item.label.toLowerCase().includes(raw) && !item.value.toLowerCase().includes(raw)) continue;
        if (!byLabel.has(item.label)) byLabel.set(item.label, item);
      }
      if (byLabel.size >= 12) break;
    }

    return Array.from(byLabel.values()).slice(0, 12);
  }, [allRows, searchQuery]);

  const selectedBac = String(selectedRow?.BAC_Code__c ?? '').trim();
  const selectedAccountId = String(selectedRow?.Account_ID__c ?? '').trim();
  const hasSelectedBac = Boolean(selectedBac);

  const dealerEnrollments = useMemo(
    () => flattenEnrollmentsForBac(allEnrollmentData, hasSelectedBac ? selectedBac : ''),
    [allEnrollmentData, hasSelectedBac, selectedBac]
  );

  const programCardModels = useMemo(
    () => dealerEnrollments.map((row) => buildProgramCardModel(row)),
    [dealerEnrollments]
  );

  const combinedSelectionRowsForTable = useMemo(
    () => buildCombinedSelectionRowsForTable(dealerEnrollments),
    [dealerEnrollments]
  );

  const relatedBuySells = useMemo(() => {
    const rows = Array.isArray(buySellData) ? buySellData : [];
    if (!selectedAccountId) return [];
    const target = selectedAccountId.toLowerCase();
    return rows.filter((row) => {
      const selling = String(row?.accountId || '').trim().toLowerCase();
      const buying = String(row?.buySellAccountId || '').trim().toLowerCase();
      return selling === target || buying === target;
    });
  }, [buySellData, selectedAccountId]);

  const buySellColumns = useMemo(
    () => [
      { field: 'accountName', headerName: 'Account Name', flex: 1.2, minWidth: 220 },
      { field: 'accountId', headerName: 'Selling Account ID', flex: 1, minWidth: 190 },
      { field: 'buySellAccountId', headerName: 'Buying Account ID', flex: 1, minWidth: 190 },
      { field: 'buySellStage', headerName: 'Stage', width: 120 },
      { field: 'stageDate', headerName: 'Stage Date', width: 130 },
      { field: 'bac', headerName: 'BAC', width: 110 },
      { field: 'lastEmailSentAt', headerName: 'Last Alert', minWidth: 180, flex: 1 },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns: tableColumns,
    data: filteredRows,
    localization: MRT_Localization_EN,
    getRowId: (row) => String(row?.__rowId ?? row?.Id ?? row?.id ?? Math.random()),
    enableRowSelection: false,
    enableColumnOrdering: true,
    enableColumnDragging: false,
    enableColumnResizing: true,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enablePagination: true,
    enableGlobalFilter: false,
    enableColumnFilters: true,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableSorting: true,
    enableRowVirtualization: true,
    state: {
      columnFilters,
      sorting,
      columnVisibility,
      columnOrder,
      columnPinning,
      isLoading: isLoadingRows,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    initialState: {
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 100 },
      columnPinning,
      columnOrder: columnOrder.length ? columnOrder : undefined,
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100, 250],
    },
    renderColumnActionsMenuItems: ({ column, closeMenu, internalColumnMenuItems, table }) => {
      const canFilter = column.getCanFilter?.();
      if (!canFilter) return internalColumnMenuItems;

      const addFilterItem = (
        <MenuItem
          key="add-filter"
          onClick={() => {
            setPendingFilterColumnId(column.id);
            closeMenu?.();
          }}
        >
          <FilterAltIcon fontSize="small" style={{ marginRight: 8 }} />
          Add Filter
        </MenuItem>
      );

      const internalItems = Array.isArray(internalColumnMenuItems)
        ? [...internalColumnMenuItems]
        : internalColumnMenuItems
          ? [internalColumnMenuItems]
          : [];

      const getMenuItemLabel = (node) => {
        if (!node) return '';
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(getMenuItemLabel).join('');
        if (typeof node === 'object') {
          if (node.props?.label) return node.props.label;
          if (node.props?.children) return getMenuItemLabel(node.props.children);
        }
        return '';
      };

      const clearFilterLabel = table?.options?.localization?.clearFilter || 'Clear Filter';
      const normalizedClearFilterLabel = clearFilterLabel.replace(/\s+/g, ' ').trim().toLowerCase();
      const clearFilterIndex = internalItems.findIndex((item) => {
        const label = getMenuItemLabel(item);
        const normalizedLabel = label.replace(/\s+/g, ' ').trim().toLowerCase();
        return normalizedLabel === normalizedClearFilterLabel;
      });

      if (clearFilterIndex === -1) return [addFilterItem, ...internalItems];
      internalItems.splice(clearFilterIndex, 0, addFilterItem);
      return internalItems;
    },
    muiTableHeadCellProps: ({ column }) => {
      const isSorted = Boolean(column.getIsSorted?.());
      return {
        sx: {
          pl: 1.5,
          '& .Mui-TableHeadCell-Content button[aria-label*="Filter by"]': {
            opacity: 0,
            pointerEvents: 'none',
          },
          '& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root': {
            display: 'inline-flex',
          },
          '& .MuiTableSortLabel-root': {
            display: isSorted ? 'inline-flex' : 'none',
          },
        },
      };
    },
    muiTableBodyCellProps: {
      sx: {
        pl: 1.5,
      },
    },
    renderToolbarInternalActions: ({ table }) => {
      const {
        columnFilterDisplayMode,
        enableColumnFilters,
        enableColumnOrdering,
        enableColumnPinning,
        enableDensityToggle,
        enableFilters,
        enableFullScreenToggle,
        enableGlobalFilter,
        enableHiding,
        initialState,
      } = table.options;
      const { columnFilters: activeColumnFilters } = table.getState();
      const hasActiveFilters = (activeColumnFilters?.length || 0) > 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {enableFilters && enableGlobalFilter && !(initialState?.showGlobalFilter) ? (
            <MRT_ToggleGlobalFilterButton table={table} />
          ) : null}
          <Tooltip title="Reset filters">
            <span>
              <IconButton size="small" onClick={() => setColumnFilters([])} disabled={!hasActiveFilters}>
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {enableFilters && enableColumnFilters && columnFilterDisplayMode !== 'popover' ? (
            <MRT_ToggleFiltersButton table={table} />
          ) : null}
          {(enableHiding || enableColumnOrdering || enableColumnPinning) ? (
            <MRT_ShowHideColumnsButton table={table} />
          ) : null}
          {enableDensityToggle ? <MRT_ToggleDensePaddingButton table={table} /> : null}
          {enableFullScreenToggle ? <MRT_ToggleFullScreenButton table={table} /> : null}
        </Box>
      );
    },
    muiTableBodyRowProps: ({ row }) => ({
      hover: true,
      onClick: (event) => {
        const target = event?.target;
        if (target?.closest?.('button, a, input, textarea, [role="button"]')) return;
        setSelectedRow(row.original);
        setDrawerTab('account');
        setDrawerOpen(true);
      },
      sx: { cursor: 'pointer' },
    }),
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 280px)',
      },
    },
  });

  useEffect(() => {
    if (!pendingFilterColumnId || !table) return;
    return scheduleMrtFilterPopoverOpen({
      table,
      columnId: pendingFilterColumnId,
      buttonSelector: 'button[aria-label*="Filter by"]',
      onComplete: () => setPendingFilterColumnId(null),
    });
  }, [pendingFilterColumnId, table]);

  return (
    <Box sx={{ maxWidth: 1660, mx: 'auto', py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2 } }}>
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 4,
          p: { xs: 2, sm: 3 },
          borderColor: 'rgba(10,42,65,0.22)',
          background:
            'linear-gradient(120deg, rgba(10,42,65,0.18), rgba(255,255,255,0) 47%), linear-gradient(300deg, rgba(1,85,77,0.14), rgba(255,255,255,0) 42%)',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
          {isWipRoute ? <Chip label="WIP" color="warning" size="small" /> : null}
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            AppSheet Accounts Browser
          </Typography>
        </Box>
        <Typography sx={{ color: 'text.secondary', mb: 2.25, maxWidth: 920 }}>
          Search, segment, and investigate <strong>{ACCOUNTS_TABLE_NAME}</strong> records without context switching to AppSheet.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Cached rows: ${allRows.length.toLocaleString()}`} color="primary" variant="outlined" size="small" />
          <Chip label={`Fields: ${(tableMeta?.fields || []).length}`} variant="outlined" size="small" />
          <Chip label={`Result rows: ${filteredRows.length.toLocaleString()}`} variant="outlined" size="small" />
          {tableMeta?.cacheUpdatedAt ? (
            <Chip label={`Cache updated: ${new Date(tableMeta.cacheUpdatedAt).toLocaleString()}`} variant="outlined" size="small" />
          ) : null}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            freeSolo
            options={searchSuggestions}
            value={null}
            inputValue={searchQuery}
            onInputChange={(_event, value) => setSearchQuery(value || '')}
            onChange={(_event, option) => {
              if (!option) return;
              if (typeof option === 'string') {
                setSearchQuery(option);
                return;
              }
              setSearchQuery(option.value || option.label || '');
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.label || '';
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Search Accounts"
                placeholder="BAC, dealer name, account ID, status..."
              />
            )}
            sx={{ minWidth: { xs: '100%', sm: 420 } }}
          />
          <Chip
            label={`Rows: ${filteredRows.length.toLocaleString()} / ${allRows.length.toLocaleString()}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      {isLoadingRows ? (
        <Paper
          variant="outlined"
          sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {loadingMessage || 'Loading rows...'}
          </Typography>
        </Paper>
      ) : (
        <MaterialReactTable table={table} />
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 1020 }, maxWidth: '100vw' } }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Tabs value={drawerTab} onChange={(_event, value) => setDrawerTab(value)}>
              <Tab value="account" label="Account" icon={<AccountCircleOutlinedIcon fontSize="small" />} iconPosition="start" />
              <Tab value="dpe" label="DPE" icon={<QueryStatsOutlinedIcon fontSize="small" />} iconPosition="start" />
              <Tab
                value="orderItems"
                label="OrderItems"
                icon={<ShoppingCartOutlinedIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                value="buySells"
                label="Buy/Sells"
                icon={<CompareArrowsOutlinedIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab value="history" label="History" icon={<HistoryOutlinedIcon fontSize="small" />} iconPosition="start" />
            </Tabs>
            <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close details">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {drawerTab === 'account' ? (
              <AppsheetAccountsAccountTabPanel
                selectedRow={selectedRow}
                allRows={allRows}
                tableFields={tableMeta?.fields || []}
                selectedAccountId={selectedAccountId}
                userKey={userKey}
                onSelectRow={(row) => {
                  setSelectedRow(row);
                  setDrawerTab('account');
                  setDrawerOpen(true);
                }}
              />
            ) : null}

            {drawerTab === 'dpe' ? (
              <AppsheetAccountsDpeTabPanel
                hasSelectedBac={hasSelectedBac}
                selectedBac={selectedBac}
                dpeError={dpeError}
                loadingDpeData={loadingDpeData}
                programCardModels={programCardModels}
                combinedSelectionRowsForTable={combinedSelectionRowsForTable}
                userEmail={user?.email || null}
              />
            ) : null}

            {drawerTab === 'orderItems' ? (
              hasSelectedBac ? (
                <DetailPanelAppSheetTab bac={selectedBac} userEmail={user?.email || null} />
              ) : (
                <Alert severity="info">OrderItems view is disabled for accounts without BAC_Code__c.</Alert>
              )
            ) : null}

            {drawerTab === 'buySells' ? (
              <AppsheetAccountsBuySellsTabPanel
                buySellsError={buySellsError}
                loadingBuySells={loadingBuySells}
                relatedBuySells={relatedBuySells}
                buySellColumns={buySellColumns}
                selectedAccountId={selectedAccountId}
              />
            ) : null}

            {drawerTab === 'history' ? (
              <AppsheetAccountsHistoryTabPanel
                bac={selectedBac}
                accountId={selectedAccountId}
                accountName={selectedRow?.Name || ''}
                rowKey={selectedRow?.Id || selectedAccountId || ''}
              />
            ) : null}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default WipAppsheetAccountsBrowserPage;
